import { useDisplay } from 'vuetify'
import { useLocalStorage } from '@vueuse/core'
import { useGetSpecialtyModels } from '@/api/consultations/queries'
import { useGetCustomPromptList } from '@/api/customizations/queries'

export const useAppStore = defineStore('app', () => {
  const { smAndUp } = useDisplay()
  const doNotShowShareTabGuide = ref(false)
  const layoutDrawer = ref(smAndUp.value)

  // Side panel (Home + sidebar) – persistido por usuário
  const userId = window.tokenParsed?.sub ?? 'guest'
  const isSidebarOpen = useLocalStorage(
    `is-sidebar-open-${userId}`,
    false,
    { writeDefaults: true },
  )
  const listGroupOpen = useLocalStorage(
    `group-lists-${userId}`,
    [],
    { writeDefaults: true },
  )
  const openedListGroups = listGroupOpen
  const selectedResultTab = ref(null)
  const isCustomizedSpecialty = ref(false)
  const selectedModelSpecialty = ref(null)
  const specialtyModels = ref(null)
  const selectedModelPreview = ref('generic')
  const customPromptList = ref(null)

  const { data: specialtyModelsQuerry, isLoading: isSpecialtyModelsLoading } =
    useGetSpecialtyModels()

  const {
    data: customPromptListQuery,
    isLoading: isCustomPromptListLoading,
    refetch: refetchCustomPromptList,
  } = useGetCustomPromptList({ page: 1, limit: 100 })

  const localModelKey = `selected-model-specialty-id-${window.tokenParsed.sub}`

  const setSelectedModelSpecialty = (item) => {
    selectedModelSpecialty.value = item
    item?.id && localStorage.setItem(localModelKey, JSON.stringify(item?.id))
  }

  //get doNotShowShareTabGuide from localstorage if exists
  doNotShowShareTabGuide.value =
    JSON.parse(
      localStorage.getItem(
        `do-not-show-share-tab-guide-${window.tokenParsed.sub}`,
      ),
    ) ?? doNotShowShareTabGuide.value

  //save doNotShowShareTabGuide in local storage
  const setDoNotShowShareTabGuide = () => {
    localStorage.setItem(
      `do-not-show-share-tab-guide-${window.tokenParsed.sub}`,
      doNotShowShareTabGuide.value,
    )
  }

  const getSpecialtyName = (specialty) => {
    return specialtyModels.value?.specialties[specialty]?.title || specialty
  }

  const preselectModelFromStorage = (forced = false) => {
    const savedId = localStorage.getItem(localModelKey)

    if ((savedId && !selectedModelSpecialty.value) || forced) {
      try {
        const parsedId = JSON.parse(savedId)

        // Buscar nas especialidades padrão
        if (specialtyModels.value?.specialties) {
          const defaultSpecialty = Object.entries(
            specialtyModels.value.specialties,
          ).find(([key]) => key === parsedId)

          if (defaultSpecialty) {
            const [specialty, { title }] = defaultSpecialty
            const item = {
              title,
              value: specialty,
              isCustom: false,
              id: specialty,
            }
            selectedModelSpecialty.value = item
            return
          }
        }

        // Buscar nas especialidades personalizadas
        if (customPromptList.value?.items) {
          const customSpecialty = customPromptList.value.items.find(
            (item) => item.id === parsedId,
          )

          if (customSpecialty) {
            const item = {
              title: customSpecialty.title,
              value: customSpecialty.specialty,
              id: customSpecialty.id,
              content: customSpecialty.content,
              specialty: customSpecialty.specialty,
              isCustom: true,
              isActive: true,
            }
            selectedModelSpecialty.value = item
            return
          }
        }
      } catch (error) {
        console.warn('Erro parsing ID saved in localStorage:\n', error)
      }
    }
  }

  watch(doNotShowShareTabGuide, (value) => {
    setDoNotShowShareTabGuide()
  })

  watch(
    specialtyModelsQuerry,
    (value) => {
      specialtyModels.value = value
    },
    { immediate: true },
  )

  watch(
    customPromptListQuery,
    (value) => {
      customPromptList.value = value
    },
    { immediate: true },
  )

  // Watcher para executar pré-seleção quando os dados estão carregados
  watch(
    [
      specialtyModels,
      customPromptList,
      isSpecialtyModelsLoading,
      isCustomPromptListLoading,
    ],
    () => {
      if (
        !isSpecialtyModelsLoading.value &&
        !isCustomPromptListLoading.value &&
        (specialtyModels.value || customPromptList.value)
      ) {
        preselectModelFromStorage()
      }
    },
    { immediate: true, deep: true },
  )

  // Watcher para atualizar selectedModelSpecialty quando custom prompt selecionado for modificado
  watch(
    customPromptList,
    (newCustomPromptList) => {
      if (
        selectedModelSpecialty.value?.isCustom &&
        newCustomPromptList?.items
      ) {
        const updatedCustomPrompt = newCustomPromptList.items.find(
          (item) => item.id === selectedModelSpecialty.value.id,
        )
        if (updatedCustomPrompt) {
          // Atualiza o selectedModelSpecialty com os dados mais recentes
          selectedModelSpecialty.value = {
            title: updatedCustomPrompt.title,
            value: updatedCustomPrompt.specialty,
            id: updatedCustomPrompt.id,
            content: updatedCustomPrompt.content,
            specialty: updatedCustomPrompt.specialty,
            isCustom: true,
            isActive: true,
          }
        } else {
          // Custom prompt foi deletado, limpa a seleção
          selectedModelSpecialty.value = null
          localStorage.removeItem(localModelKey)
        }
      }
    },
    { deep: true },
  )

  return {
    doNotShowShareTabGuide,
    layoutDrawer,
    isSidebarOpen,
    openedListGroups,
    selectedResultTab,
    isCustomizedSpecialty,
    selectedModelSpecialty,
    specialtyModels,
    customPromptList,
    isSpecialtyModelsLoading,
    isCustomPromptListLoading,
    selectedModelPreview,
    setSelectedModelSpecialty,
    setDoNotShowShareTabGuide,
    getSpecialtyName,
    preselectModelFromStorage,
    refetchCustomPromptList,
  }
})
